<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {ref} from "vue";
import SearchField from "@/components/forms/SearchField.vue";
import {DataTableHeader, useDisplay} from "vuetify/framework";
import SortField from "@/components/forms/SortField.vue";
import {Filters, Member, Team, TeamRole} from "@/types";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import NavigationTabWithChip from "@/components/tabs/NavigationTabWithChip.vue";
import TeamRightDrawer from "@/Pages/teams/components/TeamRightDrawer.vue";
import RoleSelect from "@/Pages/teams/components/RoleSelect.vue";
import AddMemberDialog from "@/Pages/teams/components/AddMemberDialog.vue";
import {useRole} from "@/composables/role";
import IconDateTooltip from "@/components/tooltips/IconDateTooltip.vue";
import {useAuth} from "@/composables/auth";
import MeChip from "@/components/chips/MeChip.vue";
import TeamActionsBtn from "@/Pages/teams/components/TeamActionsBtn.vue";
import MemberActionsBtn from "@/Pages/teams/components/MemberActionsBtn.vue";
import TitleAvatar from "@/components/avatars/TitleAvatar.vue";

defineOptions({layout: BaseLayout})

const {mobile} = useDisplay()
const {user} = useAuth()
const {isOwner, isAdminOrOwner} = useRole()

interface Props {
    title: string
    team: Team
    hasJoined: boolean
    category: 'members'
    items: Array<Member>
    internalRoles: Array<string>
    filters: Filters
    count: number
}

const props = defineProps<Props>();

const search = ref<string | null>(props.filters.search)
const sortBy = ref<string>(props.filters.sort_by)
const sortDir = ref<'asc' | 'desc'>(props.filters.sort_dir)

const isSidebarOpen = ref<boolean>(true);
const isAddMemberDialogOpen = ref<boolean>(false);

const tab = ref<'members'>(props.category);

const sortItems: Array<{ title: string; value: string }> = [
    {
        title: 'Account',
        value: 'name'
    },
    {
        title: 'Access granted',
        value: 'joined_at'
    },
    {
        title: 'Role',
        value: 'role'
    },
    {
        title: 'User created',
        value: 'user_created_at'
    }
]

const headers: Array<DataTableHeader> = [
    {
        title: 'Account',
        value: 'name',
        sortable: false,
        align: 'start',
        width: '40%',
    },
    {
        title: 'Role',
        value: 'role',
        sortable: false,
        align: 'start',
        width: '10%',
    },
    {
        title: 'Internal role',
        value: 'internal_role',
        sortable: false,
        align: 'start',
        width: '20%',
    },
    {
        title: 'Activity',
        value: 'activity',
        sortable: false,
        align: 'start',
        width: '20%',
    },
    {
        title: '',
        value: 'actions',
        sortable: false,
        align: 'end',
        width: '5%',
    },
]

const loadItems = () => {
    const params: Record<string, string | null> = {
        search: search.value,
        sort_by: sortBy.value,
        sort_dir: sortDir.value,
    };

    // Remove empty parameters
    Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
            delete params[key];
        }
    });

    router.get(
        route('teams.show', {team: props.team.id}),
        params,
        {
            preserveState: true,
        }
    );
}

const updateUserRole = (userId: number, newRole: TeamRole) => {
    router.put(
        route('teams.users.update', {team: props.team.id, user: userId}),
        {
            role: newRole,
        }
    );
}

const editResource = () => {
    router.get(route('teams.edit', {team: props.team.id}));
}

const deleteResource = () => {
    router.delete(route('teams.destroy', {team: props.team.id}),);
}

const removeMember = (userId: number) => {
    router.delete(route('teams.users.destroy', {team: props.team.id, user: userId}),);
}
</script>

<template>
    <team-right-drawer
        v-model="isSidebarOpen"
        :team="props.team"
    />
    <v-container>
        <v-row class="align-center justify-space-between">
            <h1 class="text-h4 font-weight-bold">
                <title-avatar
                    :id="props.team.id"
                    :title="props.team.name"
                    size="48"
                />
                {{ props.team.short_name }}
            </h1>
            <div>
                <v-btn
                    v-if="isAdminOrOwner"
                    :class="mobile ? 'mt-3' : 'mr-2'"
                    :block="mobile"
                    @click="isAddMemberDialogOpen = true"
                >
                    Invite members
                </v-btn>
                <v-btn
                    v-if="isOwner"
                    color="black"
                    variant="text"
                    border="sm"
                    :class="mobile ? 'mt-3' : ''"
                    :block="mobile"
                    @click="editResource"
                >
                    <span class="font-weight-regular">
                        Edit
                    </span>
                </v-btn>
                <team-actions-btn
                    :sidebar-open="isSidebarOpen"
                    :team="props.team"
                    :hidden="!hasJoined"
                    :hide-admin-actions="!isOwner"
                    @update="editResource"
                    @delete="deleteResource"
                    @leave="removeMember($event)"
                    @toggle-sidebar="isSidebarOpen = !isSidebarOpen"
                />
            </div>
        </v-row>
        <v-row class="mt-5">
            <p class="text-medium-emphasis text-pre-wrap">
                {{ props.team.description }}
            </p>
        </v-row>
        <v-row class="mt-5 mb-0">
            <v-col
                cols="12"
                class="pa-0"
            >
                <v-tabs
                    v-model="tab"
                    align-tabs="start"
                    color="primary"
                >
                    <navigation-tab-with-chip
                        :tab="tab"
                        value="members"
                        :count="props.count"
                    >
                        Members
                    </navigation-tab-with-chip>
                </v-tabs>
            </v-col>
        </v-row>
        <v-row class="mt-0 bg-grey-lighten-5 border-t-sm border-b-sm">
            <v-col
                cols="12"
                md="8"
                class="py-4"
            >
                <search-field
                    v-model="search"
                    @click="loadItems"
                />
            </v-col>
            <v-col
                cols="12"
                md="4"
                class="py-4"
            >
                <sort-field
                    v-model:sort-by="sortBy"
                    v-model:sort-dir="sortDir"
                    :items="sortItems"
                    @update:sort-by="loadItems"
                    @update:sort-dir="loadItems"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col
                cols="12"
                class="py-0"
            >
                <v-data-table
                    :headers="headers"
                    :items="props.items"
                    :items-per-page="0"
                    disable-sort
                    hide-default-footer
                    no-data-text="No results found."
                >
                    <template #no-data>
                        <v-sheet class="mt-10">
                            <v-row
                                class="fill-height"
                                align="center"
                                justify="center"
                            >
                                <v-col
                                    cols="12"
                                    class="text-center"
                                >
                                    <v-avatar size="128">
                                        <v-icon
                                            size="128"
                                            color="grey-lighten-2"
                                        >
                                            mdi-emoticon-sad-outline
                                        </v-icon>
                                    </v-avatar>
                                    <h2 class="text-h5 font-weight-bold">
                                        No members found.
                                    </h2>
                                    <p>
                                        Try adjusting your search criteria.
                                    </p>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </template>
                    <template #[`item.name`]="{ item }">
                        <v-list-item class="px-0">
                            <template #prepend>
                                <v-avatar
                                    :image="`https://ui-avatars.com/api/?name=${item.name}`"
                                />
                            </template>
                            <v-list-item-title
                                class="text-body-2 font-weight-bold text-wrap"
                            >
                                {{ item.name }}
                                <me-chip v-if="item.id === user.id" />
                            </v-list-item-title>
                            <v-list-item-subtitle
                                class="text-body-2 text-medium-emphasis"
                            >
                                {{ item.email }}
                            </v-list-item-subtitle>
                        </v-list-item>
                    </template>
                    <template #[`item.role`]="{ item }">
                        <role-select
                            v-if="isAdminOrOwner && item.id !== user.id && props.team.user_id !== item.id"
                            :hide-owner="!isOwner"
                            :value="item.role"
                            @update:model-value="updateUserRole(item.id, $event)"
                        />
                        <span
                            v-else
                            class="ml-1 text-body-2 text-capitalize"
                        >
                            {{ item.role }}
                        </span>
                    </template>
                    <template #[`item.activity`]="{ item }">
                        <icon-date-tooltip
                            icon="mdi-account-plus-outline"
                            :date="item.user_created_at"
                        >
                            User created
                        </icon-date-tooltip>
                        <icon-date-tooltip
                            v-if="item.joined_at"
                            icon="mdi-check"
                            :date="item.joined_at"
                        >
                            Access granted
                        </icon-date-tooltip>
                    </template>
                    <template #[`item.actions`]="{ item }">
                        <member-actions-btn
                            v-if="isAdminOrOwner"
                            :team="props.team"
                            :member="item"
                            :items="props.internalRoles"
                            @remove="removeMember($event)"
                        />
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <add-member-dialog
            v-if="isAdminOrOwner"
            v-model="isAddMemberDialogOpen"
            :team-id="props.team.id"
            :is-owner="isOwner"
        />
    </v-container>
</template>

<style scoped>
:deep(.v-data-table__th) {
    font-weight: 600 !important;
}
</style>
